/**
 * Meeting page store (Zustand 5, vanilla + subscribeWithSelector).
 *
 * RULE: high-frequency fields (`currentTime`, `activeSegmentId`, `activeWordIndex`) are NEVER
 * selected with `useMeeting(...)` in a component. They are read with `meetingStore.subscribe`
 * inside effects and written straight to the DOM (see hooks/useStoreEffect.ts). Only structural
 * data (segments, chapters, turns, outcomes, modes) is selected for React rendering.
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import type { AgendaChapter, ImpactReport, Legislation, Meeting, Person, Seconds, SpeakerTurn, TranscriptSegment } from "../types";
import type { MeetingDelta, MeetingSnapshot, Outcome } from "../realtime/protocol";
import { isRecordId } from "../lib/records";

export type FollowMode = "following" | "paused" | "annotating";

export interface MeetingState {
  // structural (rendered by React)
  meeting: Meeting | null;
  chapters: AgendaChapter[];
  turns: SpeakerTurn[];
  segments: TranscriptSegment[];
  outcomes: Outcome[];
  people: Record<string, Person>;
  report: ImpactReport | null;
  legislation: Legislation[];
  agenda: Legislation[];
  frames: { t: Seconds; url: string }[];
  segmentPages: number;      // a long meeting's later segments, in pages fetched after paint (realtime/bootstrap.ts allSegments)
  followMode: FollowMode;
  transcriptHidden: boolean;
  skipProcedure: boolean;
  seq: number;

  // transient (subscribe only; never select in render)
  currentTime: Seconds;
  activeSegmentId: string | null;
  activeWordIndex: number;

  // actions
  hydrate: (snap: MeetingSnapshot) => void;
  applyDelta: (seq: number, d: MeetingDelta) => void;
  tick: (t: Seconds) => void;
  setFollowMode: (m: FollowMode) => void;
  setTranscriptHidden: (v: boolean) => void;
  setSkipProcedure: (v: boolean) => void;
}

/** Binary search: the last segment whose start <= t. Segments are sorted by start. */
function findSegmentIndex(segments: TranscriptSegment[], t: Seconds): number {
  let lo = 0, hi = segments.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (segments[mid].start <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
}

export const meetingStore = createStore<MeetingState>()(
  subscribeWithSelector((set, get) => ({
    meeting: null,
    chapters: [],
    turns: [],
    segments: [],
    outcomes: [],
    people: {},
    segmentPages: 0,
    report: null,
    legislation: [],
    agenda: [],
    frames: [],
    followMode: "following",
    transcriptHidden: false,
    skipProcedure: false,
    seq: 0,
    currentTime: 0,
    activeSegmentId: null,
    activeWordIndex: -1,

    hydrate: (snap) =>
      set({
        meeting: snap.meeting,
        // Record numbers only (an older analysis may have saved a model's "unknown" or the prompt's example).
        chapters: snap.chapters.map((c) => (c.legislationIds ? { ...c, legislationIds: c.legislationIds.filter((id) => isRecordId(id)) } : c)),
        turns: snap.turns,
        segments: snap.segments,
        outcomes: snap.outcomes,
        people: snap.people ?? {},
        report: snap.report ?? null,
        legislation: snap.legislation ?? [],
        agenda: snap.agenda ?? [],
        frames: snap.frames ?? [],
        segmentPages: snap.segmentPages ?? 0,
        seq: snap.seq,
      }),

    applyDelta: (seq, d) => {
      if (seq <= get().seq) return; // already applied (replay overlap)
      switch (d.t) {
        case "segment.append":
          set((s) => ({ seq, segments: [...s.segments, d.segment] }));
          break;
        case "segment.patch":
          set((s) => ({
            seq,
            segments: s.segments.map((x) => (x.id === d.id ? { ...x, text: d.text, words: d.words ?? x.words } : x)),
          }));
          break;
        case "turn.append":
          set((s) => ({ seq, turns: [...s.turns, d.turn] }));
          break;
        case "outcome.set":
          set((s) => ({
            seq,
            outcomes: s.outcomes.some((o) => o.id === d.outcome.id)
              ? s.outcomes.map((o) => (o.id === d.outcome.id ? d.outcome : o))
              : [...s.outcomes, d.outcome],
          }));
          break;
        case "status":
          set((s) => ({ seq, meeting: s.meeting ? { ...s.meeting, status: d.status } : s.meeting }));
          break;
      }
    },

    /** Called from the video's rAF loop. Only writes derived fields when they actually change. */
    tick: (t) => {
      const s = get();
      const i = findSegmentIndex(s.segments, t);
      const seg = i >= 0 ? s.segments[i] : null;
      let w = -1;
      if (seg?.words) {
        for (let k = seg.words.length - 1; k >= 0; k--) if (seg.words[k].start <= t) { w = k; break; }
      }
      const next: Partial<MeetingState> = { currentTime: t };
      if ((seg?.id ?? null) !== s.activeSegmentId) next.activeSegmentId = seg?.id ?? null;
      if (w !== s.activeWordIndex) next.activeWordIndex = w;
      set(next);
    },

    setFollowMode: (followMode) => set({ followMode }),
    setTranscriptHidden: (transcriptHidden) => set({ transcriptHidden }),
    setSkipProcedure: (skipProcedure) => set({ skipProcedure }),
  })),
);

export const useMeeting = <T,>(selector: (s: MeetingState) => T) => useStore(meetingStore, selector);
